import { StateCreator } from "zustand";
import { Database, supabase } from "../../../../lib/supabase";
import { Logger } from "../../../../lib/logger/Logger";
import { showNotification } from "./selectors";
import { State } from "./types";
import { Region } from "../../types/enums";
import { SupabaseWriteTableEnum } from "../../../../lib/supabase/supabaseTypes";
import { useCentralStore } from ".";
import { useAttributesStore, onboardingStatusToValue, OnboardingAttributeStatus } from "../../store/Attributes";

export interface OnboardingSlice {
	updateOrganizationDefault: (key: keyof Database["public"]["Tables"]["organizations"]["Row"], value: any) => Promise<void>;
	createOrUpdateOnboardingRegion: (data: {
		region: Region;
		is_mwst_eust: boolean;
		mwst_number: string | null;
		mwst_percentage: number;
		currency_text: string;
		round_to: number;
	}) => Promise<number | null>;
	createOrUpdateOnboardingBankAccount: (data: {
		account_name: string | null;
		bank_name: string | null;
		bank_city: string | null;
		qr_iban: string | null;
		reference_number: string | null;
		cdtr_name: string | null;
		cdtr_street: string | null;
		cdtr_house_number: string | null;
		cdtr_city: string | null;
		cdtr_postal_code: string | null;
		cdtr_country: string | null;
	}) => Promise<number | null>;
	UpdateOnboardingPriceList: (data: {
		region: Region;
		tp_value: number;
		start_date: string;
	}) => Promise<number | null>;
	hasAttributeValue: (attribute: string, value?: string) => boolean;
}

export const createOnboardingSlice: StateCreator<
	State & OnboardingSlice,
	[],
	[],
	OnboardingSlice
> = (set, get) => ({
	updateOrganizationDefault: async (key: keyof Database["public"]["Tables"]["organizations"]["Row"], value: any) => {
		const { organization } = get();

		if (!organization?.id) {
			Logger.error("No organization ID when updating default");
			return;
		}

		try {
			const { error } = await supabase
				.from(SupabaseWriteTableEnum.ORGANIZATIONS)
				.update({ [key]: value } as Database['public']['Tables']['organizations']['Update'])
				.eq("id", organization.id);

			if (error) {
				Logger.error(`Error updating ${key}: ${error.message}`);
				showNotification({
					message: "Fehler beim Aktualisieren",
					type: "error",
				});
				return;
			}

			// Update local organization state directly
			set({
				organization: {
					...organization,
					[key]: value
				}
			});
		} catch (error) {
			Logger.error(`Error updating ${key}: ${String(error)}`);
			showNotification({
				message: "Fehler beim Aktualisieren",
				type: "error",
			});
		}
	},
	createOrUpdateOnboardingRegion: async (data) => {
		const { organization } = get();

		if (!organization?.id) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return null;
		}

		try {
			const regionData = {
				organization_id: organization.id,
				region: data.region,
				is_mwst_eust: data.is_mwst_eust,
				mwst_number: data.mwst_number,
				mwst_percentage: data.mwst_percentage,
				currency_text: data.currency_text,
				round_to: data.round_to,
			};

			const { data: result, error } = await supabase
				.from("activated_regions")
				.upsert(regionData, {
					onConflict: "organization_id,region",
				})
				.select()
				.single();

			if (error) {
				Logger.error("Error saving region: " + error.message);
				showNotification({
					message: "Fehler beim Speichern der Region",
					type: "error",
				});
				return null;
			}

			if (result?.id) {
				// Update organization default
				await useCentralStore.getState().updateOrganizationDefault(
					"default_activated_region_id",
					result.id
				);
				// Refresh regions in store
				await useCentralStore.getState().loadRegions();
				return result.id;
			}

			return null;
		} catch (error) {
			Logger.error("Error creating/updating region: " + String(error));
			showNotification({
				message: "Fehler beim Speichern der Region",
				type: "error",
			});
			return null;
		}
	},

	createOrUpdateOnboardingBankAccount: async (data) => {
		const { organization } = get();

		if (!organization?.id) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return null;
		}

		try {
			// Remove whitespace from IBAN
			const cleanedIban = data.qr_iban?.replace(/\s/g, "") || null;

			const isSwissIban = cleanedIban?.startsWith("CH");
			const currency = isSwissIban ? "CHF" : "EUR";

			const bankData = {
				organization_id: organization.id,
				account_name: data.account_name,
				bank_name: data.bank_name,
				bank_city: data.bank_city,
				qr_iban: cleanedIban,
				pc_account: null,
				is_pc_account: false,
				reference_number: data.reference_number,
				currency: currency,
				// Use provided address or fall back to organization address
				cdtr_name: data.cdtr_name || organization.name || null,
				cdtr_street_line_1: data.cdtr_street || null,
				cdtr_house_number_line_2: data.cdtr_house_number || null,
				cdtr_city: data.cdtr_city || organization.city || null,
				cdtr_area_code: data.cdtr_postal_code || organization.postal_code || null,
				cdtr_country: data.cdtr_country || organization.country || null,
				cdtr_adrtp: "S",
			};

			// Check if default bank account exists
			const existingId = organization.default_bank_account_id;

			if (existingId) {
				// Update existing
				const { error } = await supabase
					.from("bank_accounts")
					.update(bankData)
					.eq("id", existingId)
					.eq("organization_id", organization.id);

				if (error) {
					Logger.error("Error updating bank account: " + error.message);
					showNotification({
						message: "Fehler beim Speichern des Bankkontos",
						type: "error",
					});
					return null;
				}

				// Refresh bank accounts in store
				await useCentralStore.getState().getBankAccounts();
				return existingId;
			} else {
				// Create new
				const { data: result, error } = await supabase
					.from("bank_accounts")
					.insert(bankData)
					.select()
					.single();

				if (error) {
					Logger.error("Error creating bank account: " + error.message);
					showNotification({
						message: "Fehler beim Speichern des Bankkontos",
						type: "error",
					});
					return null;
				}

				if (result?.id) {
					// Update organization default
					await useCentralStore.getState().updateOrganizationDefault(
						"default_bank_account_id",
						result.id
					);
					// Refresh bank accounts in store
					await useCentralStore.getState().getBankAccounts();
					return result.id;
				}

				return null;
			}
		} catch (error) {
			Logger.error("Error creating/updating bank account: " + String(error));
			showNotification({
				message: "Fehler beim Speichern des Bankkontos",
				type: "error",
			});
			return null;
		}
	},

	UpdateOnboardingPriceList: async (data) => {
		const { organization } = get();

		if (!organization?.id) {
			showNotification({
				message: "Organisation nicht gefunden",
				type: "error",
			});
			return null;
		}

		try {
			const priceListData = {
				organization_id: organization.id,
				region: data.region,
				tp_value: data.tp_value,
				start_date: data.start_date,
				end_date: null,
				is_deactivated: false,
			};

			// Check if default price list exists
			const existingId = organization.default_price_list_id;

			if (existingId) {
				// Update existing
				const { error } = await supabase
					.from("price_lists")
					.update(priceListData)
					.eq("id", existingId)
					.eq("organization_id", organization.id);

				if (error) {
					Logger.error("Error updating price list: " + error.message);
					showNotification({
						message: "Fehler beim Speichern der Preisliste",
						type: "error",
					});
					return null;
				}

				// Refresh price lists in store
				await useCentralStore.getState().loadPriceLists();
				return Number(existingId);
			}

			return null;
		} catch (error) {
			Logger.error("Error creating/updating price list: " + String(error));
			showNotification({
				message: "Fehler beim Speichern der Preisliste",
				type: "error",
			});
			return null;
		}
	},
	hasAttributeValue: (attribute: string, value?: string) => {
		const attributeValue = useAttributesStore.getState().getOnboardingAttribute(attribute);

		if (!attributeValue) return false;

		return value ? attributeValue === value : true;
	},
});